import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import _ from 'lodash';
import { BreadcrumbService, CslBreadcrumbItem, ModalService, Note, NotesSidebarComponent, UserLite } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { FinishingToRollingTransfer, FinishingToRollingTransferStatus } from '@app/models/inventory/finishing-to-rolling-transfer';
import { Roll } from '@app/models/knitting/roll';
import { Fabric, WorkOrder } from '@app/models/order/work-order';
import { FinishingRoute, ProcessKey } from '@app/models/production/finishing-route';
import { ProductionCard } from '@app/models/production/production-card';
import { ProductionCardBundle } from '@app/models/production/production-card/production-card-bundle';
import { FinishingRoles } from '@app/models/roles/roles-group';
import { FinishingToRollingTransferProxyService } from '@app/proxies/inventory/finishing-to-rolling-transfer.proxy.service';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { FinishingRouteUtil } from '@app/utilities/finishing-route.util';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { ConsumedItemModalComponent } from '../_modals/consumed-item-modal/consumed-item-modal.component';
import { ReorganizeProcessModalComponent } from '../_modals/reorganize-process-modal/reorganize-process-modal.component';
import { SecondQualityRollModalComponent } from '../_modals/second-quality-roll-modal/second-quality-roll-modal.component';

@Component({
  selector: 'app-finishing-route-detail',
  templateUrl: './finishing-route-detail.component.html',
  styleUrl: './finishing-route-detail.component.scss',
})
export class FinishingRouteDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly finishingRouteProxyService = inject(FinishingRouteProxyService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly individualProductionCardService = inject(IndividualProductionCardService);
  private readonly modalService = inject(ModalService);
  private readonly finishingToRollingTransferProxy = inject(FinishingToRollingTransferProxyService);
  private readonly authService = inject(UserAuthenticationService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly reportProxyService = inject(ReportProxyService);

  finishingRoute = signal<FinishingRoute | null>(null);
  rolls = signal<Roll[]>([]);
  isLoading = signal<boolean>(true);
  productionCardNumber = signal<string | null>(null);
  finishingToRollingTransfer = signal<FinishingToRollingTransfer | null>(null);
  private readonly routeId = computed(() => this.finishingRoute()?.id ?? null);
  notes = signal<Note[]>([]);
  userLite: UserLite | null = null;

  get workOrder(): WorkOrder | null {
    return this.individualProductionCardService.workOrder;
  }

  get productionCard(): ProductionCard | null {
    return this.individualProductionCardService.productionCard();
  }

  get fabrics(): Fabric[] {
    return this.individualProductionCardService.fabrics;
  }

  get bundles(): ProductionCardBundle[] {
    return this.individualProductionCardService.productionCardBundles;
  }

  completedOrInProgressProcesses = computed(() => {
    const route = this.finishingRoute();
    if (!route) return [];
    return [...FinishingRouteUtil.getCompletedProcesses(route), ...FinishingRouteUtil.getInProgressProcesses(route)];
  });

  editableProcesses = computed(() => {
    const route = this.finishingRoute();
    if (!route) return [];
    return FinishingRouteUtil.getPendingProcesses(route);
  });

  //TODO: REMOVE REMOVE
  // weightAllRolls(): void {
  //   this.rollProxyService.weightAllRolls(this.finishingRoute()?.number || '').subscribe({
  //     next: () => {
  //       GlobalMessageUtil.success('Success', 'All rolls weighted successfully');
  //       GlobalMessageUtil.success('Info', 'Please refresh the page to see updated weights');
  //     },
  //     error: (error) => {
  //       console.error('Error weighting all rolls:', error);
  //       GlobalMessageUtil.error('Error', 'Failed to weight all rolls');
  //     },
  //   });
  // }

  allProcessesSorted = computed(() => {
    const route = this.finishingRoute();
    if (!route) return [];

    const completed = FinishingRouteUtil.getCompletedProcesses(route);
    const inProgress = FinishingRouteUtil.getInProgressProcesses(route);
    const pending = FinishingRouteUtil.getPendingProcesses(route);

    return [...completed, ...inProgress, ...pending];
  });

  // Check if route has Rolling process anywhere pending (show general warning)
  hasRollingProcessPending = computed(() => {
    const route = this.finishingRoute();
    if (!route) return false;
    return FinishingRouteUtil.hasRollingProcessPending(route);
  });

  // Computed signal to detect if only Rolling process (ProcessId "29") remains pending
  isOnlyRollingProcessRemaining = computed(() => {
    const route = this.finishingRoute();
    if (!route) return false;
    return FinishingRouteUtil.isOnlyRollingProcessRemaining(route);
  });

  get isFinishingAdmin(): boolean {
    return this.authService.roles.includes(FinishingRoles.FinishingAdmin);
  }

  get isFinishingViewer(): boolean {
    return this.authService.roles.includes(FinishingRoles.FinishingViewer);
  }

  get canEditFinishingRoute(): boolean {
    return this.isFinishingAdmin || this.isFinishingViewer;
  }

  // Reorganize is gated by its own role, independent from FinishingRouteRollingAdmin
  get canReorganizeFinishingRoute(): boolean {
    return this.canEditFinishingRoute && this.authService.roles.includes(FinishingRoles.FinishingRouteReorganizeAdmin);
  }

  // Show "Send to Rolling" button when no transfer exists or transfer is not yet in route
  showSendToRolling = computed(() => {
    if (!this.isOnlyRollingProcessRemaining() || !this.canEditFinishingRoute) return false;
    const transfer = this.finishingToRollingTransfer();
    if (_.isNil(transfer)) return true;
    return (
      transfer.status === FinishingToRollingTransferStatus.Draft || transfer.status === FinishingToRollingTransferStatus.ReadyForTransfer
    );
  });

  showReceiveFromFinishing = computed(() => {
    if (!this.canEditFinishingRoute) return false;
    const transfer = this.finishingToRollingTransfer();
    return !_.isNil(transfer) && transfer.status === FinishingToRollingTransferStatus.InRoute;
  });

  // Check if transfer has been received (location is now Rolling)
  isLocationRolling = computed(() => {
    const transfer = this.finishingToRollingTransfer();
    return !_.isNil(transfer) && transfer.status === FinishingToRollingTransferStatus.Received;
  });

  // Check if route is awaiting QA
  isAwaitingQA = computed(() => {
    const route = this.finishingRoute();
    if (!route) return false;
    return FinishingRouteUtil.isAwaitingQA(route);
  });

  // Check if route is QA approved
  isQAApproved = computed(() => {
    const route = this.finishingRoute();
    if (!route) return false;
    return FinishingRouteUtil.isQAApproved(route);
  });

  // Get the transfer number for display
  transferNumber = computed(() => {
    const transfer = this.finishingToRollingTransfer();
    return transfer?.number ?? null;
  });

  // Filter rolls that were created as second quality (reasonOfCreation = 1)
  secondQualityRolls = computed(() => {
    return this.rolls().filter((roll) => roll.reasonOfCreation === 0);
  });

  showTransferReport = computed(() => {
    const transfer = this.finishingToRollingTransfer();
    if (!transfer) return false;
    return transfer.status === FinishingToRollingTransferStatus.InRoute || transfer.status === FinishingToRollingTransferStatus.Received;
  });

  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(
      () => {
        const finishingRoute = this.finishingRoute();
        const pcNumber = this.productionCardNumber();
        if (_.isNil(finishingRoute) || _.isNil(pcNumber)) return;

        const crumbs: CslBreadcrumbItem[] = [{ label: 'Planning' }];
        const workOrder = this.workOrder;
        if (!_.isNil(workOrder)) {
          crumbs.push({ label: workOrder.orderNumber ?? '', route: '/production-cards-details' });
        }
        crumbs.push({ label: pcNumber, route: `/production-card/${pcNumber}/details` });
        crumbs.push({ label: `Finishing Route - ${finishingRoute.number}` });
        this.breadcrumbService.set(crumbs);
      },
      { allowSignalWrites: true },
    );
    toObservable(this.routeId)
      .pipe(
        distinctUntilChanged(),
        filter((id): id is string => !_.isNil(id)),
        switchMap(() => this.fetchFinishingToRollingTransfer(this.finishingRoute()!)),
        takeUntilDestroyed(),
      )
      .subscribe((transfer) => this.finishingToRollingTransfer.set(transfer));
  }

  ngOnInit(): void {
    this.userLite = this.authService.userLite;

    this.route.params
      .pipe(
        tap(() => this.isLoading.set(true)),
        tap((params) => {
          this.productionCardNumber.set(params['productionCardNumber']);
        }),
        switchMap((params) => {
          const productionCardNumber = params['productionCardNumber'];
          return this.individualProductionCardService.initialize(productionCardNumber);
        }),
        switchMap(() => {
          const params = this.route.snapshot.params;
          const finishingRouteNumber = params['finishingRouteNumber'];
          return this.finishingRouteProxyService.getByNumber(finishingRouteNumber);
        }),
        tap((response) => {
          if (response.success && response.data) {
            this.finishingRoute.set(response.data);
            this.notes.set(response.data.notes || []);
            this.loadRolls(response.data.rollNumbers);
          }
        }),
        catchError((error) => {
          console.error('Error loading finishing route:', error);
          this.isLoading.set(false);
          return of(null);
        }),
      )
      .subscribe();
  }

  private loadRolls(rollNumbers: string[]): void {
    if (!rollNumbers || rollNumbers.length === 0) {
      this.isLoading.set(false);
      return;
    }

    this.rollProxyService
      .getRollsByNumbers(rollNumbers)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.rolls.set(response.data);
          }
          this.isLoading.set(false);
        }),
        catchError(() => {
          this.isLoading.set(false);
          return of(null);
        }),
      )
      .subscribe();
  }

  private fetchFinishingToRollingTransfer(route: FinishingRoute): Observable<FinishingToRollingTransfer | null> {
    return this.finishingToRollingTransferProxy
      .getByInitiator({
        reference: route.id!,
        entityDefinitionCode: 35,
        number: route.number,
      })
      .pipe(
        map((response) =>
          response.success ? (response.data?.find((t) => t.status !== FinishingToRollingTransferStatus.Voided) ?? null) : null,
        ),
        catchError(() => of(null)),
      );
  }

  isProcessEditable(process: ProcessKey): boolean {
    return _.isNil(process.startTime) && _.isNil(process.endTime);
  }

  getProcessStrict(item: unknown): ProcessKey {
    return item as ProcessKey;
  }

  isProcessStarted(process: ProcessKey): boolean {
    return !!process.startTime;
  }

  isProcessCompleted(process: ProcessKey): boolean {
    return !!process.endTime;
  }

  isProcessInProgress(process: ProcessKey): boolean {
    return this.isProcessStarted(process) && !this.isProcessCompleted(process);
  }

  getProcessStatus(process: ProcessKey): 'pending' | 'in-progress' | 'completed' {
    if (this.isProcessCompleted(process)) return 'completed';
    if (this.isProcessInProgress(process)) return 'in-progress';
    return 'pending';
  }

  openAdjustProcessModal(process: ProcessKey): void {
    const finishingRoute = this.finishingRoute();
    if (!finishingRoute?.id) {
      GlobalMessageUtil.error('Error', 'Finishing route ID not found');
      return;
    }

    const modalData = {
      process: process,
      finishingRouteId: finishingRoute.id,
      finishingRouteNumber: finishingRoute.number,
    };

    this.modalService.open(ConsumedItemModalComponent, { modalClass: 'modal_large' }, modalData).subscribe({
      next: (result) => {
        if ((result.action === 'save' || result.action === 'complete') && result.data?.finishingRoute) {
          this.finishingRoute.set(result.data.finishingRoute);
        }
      },
      error: (error) => {
        console.error('Error handling consumed item modal result:', error);
      },
    });
  }

  getProcessDuration(process: ProcessKey): string {
    if (!process.startTime || !process.endTime) {
      return '---';
    }
    const start = new Date(process.startTime);
    const end = new Date(process.endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  navigateToProductionCardDetails(): void {
    if (this.productionCardNumber()) {
      window.location.href = `/production-card/${this.productionCardNumber()}/details`;
    }
  }

  onReorganize(): void {
    if (!this.canReorganizeFinishingRoute) {
      return;
    }

    const modalData = {
      processes: this.finishingRoute()?.route || [],
      finishingRouteNumber: this.finishingRoute()?.number,
    };

    this.modalService.open(ReorganizeProcessModalComponent, { modalClass: 'modal_small' }, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'save' && result.data) {
          const finishingRoute = this.finishingRoute();
          if (!finishingRoute?.id) {
            GlobalMessageUtil.error(
              this.translate.instant('Finishing_Route_Detail_Error'),
              this.translate.instant('Finishing_Route_Detail_Route_ID_Not_Found'),
            );
            return;
          }

          const currentRoute = finishingRoute.route || [];

          const startedOrCompletedProcessIds = currentRoute
            .filter((p) => !_.isNil(p.startTime) || !_.isNil(p.endTime))
            .map((p) => p.processId);

          const pendingProcessIds = result.data.pendingProcesses.map((p: ProcessKey) => p.processId);

          const newRoute = [...startedOrCompletedProcessIds, ...pendingProcessIds];

          this.finishingRouteProxyService.updateRoute(finishingRoute.id, newRoute).subscribe({
            next: (response) => {
              if (response.success && response.data) {
                this.finishingRoute.set(response.data);
                GlobalMessageUtil.success(
                  this.translate.instant('Finishing_Route_Detail_Success'),
                  this.translate.instant('Finishing_Route_Detail_Processes_Reorganized'),
                );
              }
            },
            error: (error) => {
              console.error('Error updating route:', error);
            },
          });
        }
      },
      error: (error) => {
        console.error('Error handling reorganize modal result:', error);
      },
    });
  }

  onCancel(): void {
    if (this.productionCardNumber()) {
      window.location.href = `/production-card/${this.productionCardNumber()}/details`;
    }
  }

  onTransferToRolling(): void {
    const finishingRoute = this.finishingRoute();
    if (!finishingRoute?.id) {
      GlobalMessageUtil.error(
        this.translate.instant('Finishing_Route_Detail_Error'),
        this.translate.instant('Finishing_Route_Detail_Route_ID_Not_Found'),
      );
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('Finishing_Route_Detail_ConfirmSendToRollingMessage'),
      header: this.translate.instant('Finishing_Route_Detail_ConfirmSendToRollingHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('Finishing_Route_Detail_ConfirmSendToRollingAccept'),
      rejectLabel: this.translate.instant('Cancel'),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        // Create new transfer and set status to InRoute
        this.finishingToRollingTransferProxy.create(finishingRoute.id!).subscribe({
          next: (response) => {
            if (response.success && response.data) {
              const transfer = response.data;
              this.finishingToRollingTransfer.set(transfer);
              GlobalMessageUtil.success(
                'Success',
                `Transfer ${transfer.number} created successfully. Finishing Route in route to Rolling.`,
              );
            }
          },
          error: (error) => {
            console.error('Error creating transfer:', error);
          },
        });
      },
    });
  }

  onReceiveFinishingRoute(): void {
    const transfer = this.finishingToRollingTransfer();
    if (!transfer) {
      GlobalMessageUtil.error(
        this.translate.instant('Finishing_Route_Detail_Error'),
        this.translate.instant('Finishing_Route_Detail_No_Transfer_Found'),
      );
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('Finishing_Route_Detail_ConfirmReceiveMessage'),
      header: this.translate.instant('Finishing_Route_Detail_ConfirmReceiveHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('Finishing_Route_Detail_ConfirmReceiveAccept'),
      rejectLabel: this.translate.instant('Cancel'),
      acceptButtonStyleClass: 'p-button-info',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.finishingToRollingTransferProxy.updateStatus(transfer.id, FinishingToRollingTransferStatus.Received).subscribe({
          next: () => {
            this.finishingToRollingTransfer.set({
              ...transfer,
              status: FinishingToRollingTransferStatus.Received,
            });
            const message = this.translate.instant('Finishing_Route_Detail_Transfer_Received').replace('{0}', transfer.number);
            GlobalMessageUtil.success(this.translate.instant('Finishing_Route_Detail_Success'), message);
          },
          error: (error) => {
            console.error('Error updating transfer status:', error);
          },
        });
      },
    });
  }

  onViewMoreNotes(): void {
    this.openNotesModal();
  }

  onNoteAdded(): void {
    const finishingRoute = this.finishingRoute();
    if (!finishingRoute?.id) {
      GlobalMessageUtil.error(
        this.translate.instant('Finishing_Route_Detail_Error'),
        this.translate.instant('Finishing_Route_Detail_Route_ID_Not_Found'),
      );
      return;
    }

    // The csl-standard-notes component already mutates the notes array directly via unshift,
    // so this.notes() already contains the new note - no need to add it again
    this.finishingRouteProxyService.updateNotes(finishingRoute.id, this.notes()).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.notes.set(response.data.notes || []);
          this.finishingRoute.set(response.data);
          GlobalMessageUtil.success(
            this.translate.instant('Finishing_Route_Detail_Success'),
            this.translate.instant('Finishing_Route_Detail_Note_Added'),
          );
        }
      },
      error: (error) => {
        console.error('Error saving note:', error);
      },
    });
  }

  openNotesModal(): void {
    const modalData = {
      notes: this.notes(),
      userLite: this.userLite,
      viewOnly: !this.canEditFinishingRoute,
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          const newNotes = result.data.notes;
          const finishingRoute = this.finishingRoute();

          if (finishingRoute?.id) {
            // Save notes to the database
            this.finishingRouteProxyService.updateNotes(finishingRoute.id, newNotes).subscribe({
              next: (response) => {
                if (response.success && response.data) {
                  this.notes.set(response.data.notes || []);
                  this.finishingRoute.set(response.data);
                  GlobalMessageUtil.success(
                    this.translate.instant('Finishing_Route_Detail_Success'),
                    this.translate.instant('Finishing_Route_Detail_Notes_Saved'),
                  );
                }
              },
              error: (error) => {
                console.error('Error saving notes:', error);
              },
            });
          }
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
  }
  printFinishingRouteReport(): void {
    const finishingRoute = this.finishingRoute();
    if (!finishingRoute) {
      console.error('No finishing route production card available for printing.');
      return;
    }
    this.finishingRouteProxyService.printFinishingReport(finishingRoute.id ?? '').subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `FinishingReport_${finishingRoute.number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error printing finishing report:', err);
      },
    });
  }

  printTransferReport(): void {
    const transfer = this.finishingToRollingTransfer();
    if (!transfer?.id) {
      console.error('No transfer available for printing.');
      return;
    }
    this.reportProxyService.getTransferReport(transfer.id, 'finishing-to-rolling-transfer', 'finishing-to-rolling').subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TransferReport_${transfer.number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error printing transfer report:', err);
      },
    });
  }

  getStatusLabel(status: 'pending' | 'in-progress' | 'completed'): string {
    switch (status) {
      case 'completed':
        return this.translate.instant('Finishing_Route_Detail_Completed');
      case 'in-progress':
        return this.translate.instant('Finishing_Route_Detail_InProgress');
      case 'pending':
        return this.translate.instant('Finishing_Route_Detail_Pending');
      default:
        return '';
    }
  }

  getTransferInRouteMessage(): string {
    const transferNum = this.transferNumber();
    const template = this.translate.instant('Finishing_Route_Detail_TransferInRoute');
    return template.replace('{0}', transferNum ?? '');
  }

  getLocationRollingMessage(): string {
    const transferNum = this.transferNumber();
    const template = this.translate.instant('Finishing_Route_Detail_LocationRolling');
    return template.replace('{0}', transferNum ?? '');
  }

  openSecondQualityRollModal(): void {
    const finishingRoute = this.finishingRoute();
    if (!finishingRoute?.id) {
      GlobalMessageUtil.error(
        this.translate.instant('Finishing_Route_Detail_Error'),
        this.translate.instant('Finishing_Route_Detail_Route_ID_Not_Found'),
      );
      return;
    }

    const modalData = {
      finishingRoute: finishingRoute,
      secondQualityRolls: this.secondQualityRolls(),
    };

    this.modalService.open(SecondQualityRollModalComponent, { modalClass: 'modal_large' }, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'save' && result.data) {
          const newRoll = result.data.newRoll;
          // Add the new roll to the rolls signal
          this.rolls.update((currentRolls) => [...currentRolls, newRoll]);
          // Add the new roll number to the finishing route rollNumbers
          if (newRoll.number) {
            this.finishingRoute.update((route) => {
              if (route) {
                return {
                  ...route,
                  rollNumbers: [...route.rollNumbers, newRoll.number!],
                };
              }
              return route;
            });
          }
        }
      },
      error: (error) => {
        console.error('Error handling second quality roll modal result:', error);
      },
    });
  }
}
